import { MeetingResultsCubit } from '@breakoutlearning/firebase-repository/cubits/MeetingResultsCubit'
import { PreviewMeetingCubit } from '@breakoutlearning/firebase-repository/cubits/PreviewMeetingCubit'
import { GradeExplainerView } from 'components/MeetingResultsSlideView2/GradeExplainerView'
import { PageTitle } from 'components/PageTitle'
import { SpinnerCentered } from 'components/Spinner'
import { NotFoundPage } from 'pages/application/NotFoundPage'
import { useCurrentUser, useRepository } from 'hooks/auth'
import { useCubitBuilder } from 'hooks/cubits'
import { useBreakoutUser } from 'hooks/profile'
import { useRootStore } from 'hooks/rootStore'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'

export const GradeExplainerPage = observer(function GradeExplainerPage() {
  const store = useRootStore()
  const repository = useRepository()
  const authenticatedUserId = useCurrentUser().uid
  const user = useBreakoutUser()
  const sectionId = store.router.params?.sectionId?.toString() ?? ''
  const assignmentId = store.router.params?.assignmentId?.toString() ?? ''
  const roomStateId = store.router.params?.roomStateId?.toString() ?? ''
  const targetUserId = store.router.params?.userId?.toString() ?? ''
  const backToAssignments = store.router.queryParams?.returnTo === 'assignments'
  const isOwnGrade = targetUserId === authenticatedUserId
  const hasStaffRole = user.isFaculty || user.isAdmin || user.isCorre
  const meeting = useCubitBuilder(
    () =>
      new PreviewMeetingCubit({
        roomId: roomStateId,
        repository,
        currentUser: user,
        enableTalkTrack: false,
      }),
    [roomStateId, repository, user]
  )

  if (
    meeting.roomFailedToLoad ||
    meeting.roomAccessDenied ||
    meeting.slideDeckAccessError ||
    (meeting.roomState.isLoaded && !meeting.roomState.exists)
  ) {
    return <NotFoundPage />
  }

  if (
    !meeting.roomState.isLoaded ||
    !meeting.slideDeck.isLoaded ||
    !meeting.assignment.isLoaded ||
    !user.profile.isLoaded ||
    !repository.featureFlags.isLoaded
  ) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <SpinnerCentered reason="grade_explainer_context_loading" />
      </div>
    )
  }

  if (
    meeting.sectionId !== sectionId ||
    meeting.assignmentId !== assignmentId ||
    !targetUserId ||
    (!isOwnGrade && !hasStaffRole) ||
    (isOwnGrade &&
      !repository.featureFlags.data.gradeExplanationAccessEnabled) ||
    (isOwnGrade && !meeting.assignment.data.showFinalGradeToParticipants) ||
    (isOwnGrade && !meeting.assignment.data.assignmentGradingScalars)
  ) {
    return <NotFoundPage />
  }

  return (
    <GradeExplainerContent
      meeting={meeting}
      targetUserId={targetUserId}
      backToAssignments={backToAssignments}
      onBack={() => {
        if (backToAssignments)
          return store.navigateTo(
            user.isFaculty ? 'instructorStudentView' : 'home',
            undefined,
            { restoreScroll: 'true' }
          )
        if (!isOwnGrade)
          return store.navigateToInstructorAssignmentPage(
            sectionId,
            assignmentId,
            { tab: 'students' }
          )
        store.navigateTo('assignmentWithRoom', {
          sectionId,
          assignmentId,
          roomStateId,
        })
      }}
    />
  )
})

const GradeExplainerContent = observer(function GradeExplainerContent({
  meeting,
  targetUserId,
  backToAssignments,
  onBack,
}: {
  meeting: PreviewMeetingCubit
  targetUserId: string
  backToAssignments: boolean
  onBack: () => void
}) {
  const repository = useRepository()
  const { tt } = useTranslationTyped()
  const cubit = useCubitBuilder(
    () =>
      new MeetingResultsCubit(repository, {
        roomStateId: meeting.roomState.id,
        slideDeckId: meeting.slideDeckId,
        meetingCubit: meeting,
        userId: targetUserId,
        inDialog: false,
        onStudentAssignmentPage: true,
      }),
    [meeting, repository, targetUserId]
  )

  return (
    <>
      <PageTitle title={tt.meeting.results.grade_explainer.heading()} />
      <div className="h-full overflow-y-auto p-4 sm:p-8">
        <GradeExplainerView
          cubit={cubit}
          backToAssignments={backToAssignments}
          onBack={onBack}
        />
      </div>
    </>
  )
})
